import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { Button, INK } from '../abf/onboarding/review/reviewStyles';
import { Surface } from '../abf/onboarding/review/contractReviewStyles';
import { AskAxiomPanel, pcCopilotContext } from '../abf/copilot/AbfCopilotLayout';
import { money } from '../abf/board/boardShared';
import PrivateCreditBoardPage from './PrivateCreditBoardPage';
import PrivateCreditRegister from './PrivateCreditRegister';
import PrivateCreditWorkQueuePage from './PrivateCreditWorkQueuePage';
import NewFacilityLanes from './NewFacilityLanes';
import { getPcBoard, pcErrorMessage } from './api';
import type { PcBoard } from './types';
import type { PcHomeView } from './routes';

/**
 * The private credit home (PC workflow parity, slice 1), on the ABF home's pattern: one entry, three
 * views. Overview is the board (every facility, what it holds, what waits, one next action), Facilities
 * is the position register the fund already had, Work queue routes every attention item. "New facility"
 * offers the two lanes (a credit agreement PDF, or typed in) that end in the same record.
 */

const PC = '#8ad0a8';

const Hero = styled.div`
  margin: 2px 0 4px;
  .eyebrow { text-transform: uppercase; letter-spacing: .14em; font-size: 11px; font-weight: 800; color: ${INK.accent}; }
  h1 { margin: 6px 0 6px; color: ${INK.strong}; font-size: clamp(20px, 2.4vw, 26px); line-height: 1.15; }
  p { margin: 0; color: ${INK.muted}; max-width: 72ch; font-size: 14px; }
  p b { color: ${PC}; }
`;

const Seg = styled.div`
  display: inline-flex; gap: 4px; margin: 18px 0 16px; padding: 4px;
  border: 1px solid ${INK.lineStrong}; border-radius: 12px; background: ${INK.panel};
  button {
    appearance: none; border: 0; background: transparent; color: ${INK.muted}; font: inherit; font-weight: 700;
    padding: 9px 16px; border-radius: 9px; cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
    transition: background .15s, color .15s;
  }
  button .count { font-size: 11px; padding: 1px 7px; border-radius: 999px; background: ${INK.well}; color: ${INK.body}; }
  button:hover { color: ${INK.body}; }
  button[aria-selected='true'] { background: ${INK.well}; color: ${PC}; box-shadow: inset 0 0 0 1px ${INK.lineStrong}; }
  button:focus-visible { outline: 2px solid ${INK.accent}; outline-offset: 2px; }
`;

const TopRow = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px;
`;

const Cards = styled.div`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin: 4px 0 16px;
  @media (max-width: 980px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

const Card = styled.button`
  text-align: left; appearance: none; cursor: pointer; border: 1px solid ${INK.line}; border-radius: 12px;
  background: ${INK.panel}; color: ${INK.body}; padding: 14px 16px; box-shadow: inset 0 3px ${PC};
  transition: border-color .15s, background .15s;
  &:hover, &:focus-visible { border-color: ${PC}; background: ${INK.raised}; outline: none; }
  .lbl { font-size: 10px; text-transform: uppercase; letter-spacing: .07em; color: ${INK.faint}; font-weight: 800; }
  .val { font-size: 20px; margin-top: 4px; color: ${INK.strong}; font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sub { font-size: 12px; color: ${INK.muted}; margin-top: 4px; }
`;

const Note = styled.p`
  margin: 4px 0 8px; padding: 12px 15px; border: 1px solid ${INK.line}; border-left: 3px solid ${INK.accent};
  border-radius: 8px; background: ${INK.panel}; color: ${INK.muted}; font-size: 12.5px;
  b { color: ${INK.body}; }
`;

const SEGMENTS: Array<{ view: PcHomeView; label: string }> = [
  { view: 'overview', label: 'Overview' },
  { view: 'facilities', label: 'Facilities' },
  { view: 'queue', label: 'Work queue' },
];
const isView = (v: string | null): v is PcHomeView => v === 'overview' || v === 'facilities' || v === 'queue';
const MAKERS = new Set(['ADMIN', 'PLATFORM_ADMIN', 'PORTFOLIO_MANAGER', 'PM', 'OPERATIONS', 'OPERATIONS_ANALYST', 'MIDDLE_OFFICE_ADMIN', 'TRADER']);

export const PrivateCreditHome: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const requested = params.get('view');
  const view: PcHomeView = isView(requested) ? requested : 'overview';
  const setView = (next: PcHomeView) => {
    const copy = new URLSearchParams(params);
    if (next === 'overview') copy.delete('view'); else copy.set('view', next);
    setParams(copy, { replace: true });
  };
  const [choosing, setChoosing] = useState(false);
  const [board, setBoard] = useState<PcBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const maker = MAKERS.has((user?.role || '').replace(/^ROLE_/, '').toUpperCase());

  const loadSummary = useCallback(async () => {
    try { setBoard(await getPcBoard()); setError(null); }
    catch (failure) { setError(pcErrorMessage(failure, 'The summary could not be loaded.')); }
  }, []);
  // The summary cards sit above every view; the views fetch their own detail.
  useEffect(() => { void loadSummary(); }, [loadSummary]);
  const t = board?.totals;

  const body = <>
    <Hero>
      <div className="eyebrow">Credit &amp; Loans · Private Credit</div>
      <h1>Direct lending, from agreement to managed exposure</h1>
      <p>Each <b>facility</b> is a bilateral loan the fund underwrites, funds through a ticket, services and
        monitors. The board shows what each one holds and what waits on it; the queue routes every item to the
        screen that owns it; the register is the official book.</p>
    </Hero>

    <TopRow>
      <Seg role="tablist" aria-label="Private credit surface">
        {SEGMENTS.map(s => (
          <button key={s.view} role="tab" aria-selected={view === s.view} onClick={() => setView(s.view)}>
            {s.label}{s.view === 'queue' && t && t.attentionOpen > 0 && <span className="count">{t.attentionOpen}</span>}
          </button>
        ))}
      </Seg>
      {maker && <Button $variant="primary" type="button" onClick={() => setChoosing(true)}>+ New facility</Button>}
    </TopRow>

    {choosing && <NewFacilityLanes onClose={() => setChoosing(false)} />}

    {error && <Note role="alert"><b>Could not load the summary.</b> {error}</Note>}

    <Cards aria-label="Private credit summary">
      <Card type="button" onClick={() => setView('overview')}>
        <div className="lbl">Facilities</div>
        <div className="val">{t ? t.facilities : '—'}</div>
        <div className="sub">{t ? `${t.withPosition} held in a fund` : 'loading'}</div>
      </Card>
      <Card type="button" onClick={() => setView('overview')}>
        <div className="lbl">Commitment · funded</div>
        <div className="val">{t ? money(t.commitment) : '—'}</div>
        <div className="sub">{t ? `${money(t.funded)} funded · ${money(t.unfunded)} unfunded, as recorded` : 'loading'}</div>
      </Card>
      <Card type="button" onClick={() => setView('facilities')}>
        <div className="lbl">Official exposure</div>
        <div className="val">{t ? money(t.exposure) : '—'}</div>
        <div className="sub">{t ? `${money(t.marketValue)} market value on ${board?.businessDate}` : 'loading'}</div>
      </Card>
      <Card type="button" onClick={() => setView('queue')}>
        <div className="lbl">Needs attention</div>
        <div className="val" style={{ color: t && t.attentionCritical ? '#f8a5a5' : undefined }}>{t ? t.attentionOpen : '—'}</div>
        <div className="sub">{t ? (t.attentionCritical ? `${t.attentionCritical} critical` : 'nothing critical') : 'loading'}</div>
      </Card>
    </Cards>

    {view === 'overview' && <PrivateCreditBoardPage embedded />}
    {view === 'facilities' && <PrivateCreditRegister />}
    {view === 'queue' && <PrivateCreditWorkQueuePage embedded />}
  </>;

  // On its own route the home is an Ask Axiom subject (the private credit portfolio, slice 6); embedded in a
  // hub tab it is not, since the panel's subject is named by the route.
  // F-PCP-019: the hub embed is the normal way in now, so it carries the rail too (the panel is fixed-position).
  return embedded ? <AskAxiomPanel context={pcCopilotContext(view)}><div>{body}</div></AskAxiomPanel> : <AskAxiomPanel context={pcCopilotContext(view)}><Surface>{body}</Surface></AskAxiomPanel>;
};

export default PrivateCreditHome;
